/**
 * IntakeLog — HR Intake Log + Review Queue (Phase 1b, 2026-02).
 *
 * Two sub-tabs:
 *   1. Review Queue — `unknown` + parked direct-emails waiting for HR
 *   2. All Log      — every polled email in the last 14 days
 *
 * "Needs review" tile up top drives the sidebar pulse. Rows waiting >48h
 * get a red badge so nothing slips.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api, { errMsg } from '../../lib/api';
import { fmtDateTime } from '../../lib/date';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import {
  Inbox, AlertTriangle, CheckCircle2, XCircle, RefreshCw,
  UserPlus, Mail, Clock, Sparkles, Filter, ArrowRightCircle, Ban,
  Link2, Link2Off,
} from 'lucide-react';
import { toast } from 'sonner';

const C = {
  ink: '#2D2833', ink2: '#685B72', soft: '#8A7D95',
  border: '#E8E5EC', paper: '#FBF8F4',
  plum: '#4A3B5F', gold: '#B08A3A', teal: '#3B6F5D', rose: '#8A3A3A',
};

const CLASSIFICATION = {
  indeed_application: { label: 'Indeed application', bg: '#E4F1EA', fg: '#2F5F4A' },
  direct_application: { label: 'Direct email',       bg: '#EEF0F8', fg: '#3B4A78' },
  indeed_noise:       { label: 'Indeed noise',       bg: '#F1F0F4', fg: '#8A7D95' },
  unknown:            { label: 'Unknown',            bg: '#FBF3E0', fg: '#7A5E22' },
};

const CLINICS = ['Katy', 'Missouri City', 'Townelake', 'Fry Rd', 'Memorial',
                  'Brookshire', 'Tomball', 'Magnolia', 'Atascocita'];
const ROLES = ['SLP', 'CF-SLP', 'SLPA', 'PRN', 'Student-Extern',
                'Office Director/SLP', 'Admin'];

function ClassChip({ kind }) {
  const spec = CLASSIFICATION[kind] || CLASSIFICATION.unknown;
  return (
    <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold"
          style={{ background: spec.bg, color: spec.fg }}
          data-testid={`intake-class-${kind}`}>
      {spec.label}
    </span>
  );
}

function WaitingBadge({ hours, resolved }) {
  if (resolved) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px]" style={{ color: C.ok }}>
        <CheckCircle2 size={12} /> resolved
      </span>
    );
  }
  if (hours == null) return <span className="text-[11px]" style={{ color: C.soft }}>—</span>;
  const over48 = hours > 48;
  const label = hours < 1 ? `${Math.round(hours * 60)}m`
              : hours < 24 ? `${Math.round(hours)}h`
              : `${Math.round(hours / 24)}d`;
  return (
    <span
      className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded font-medium"
      style={{
        background: over48 ? '#F6E4E4' : '#F4F1F8',
        color:      over48 ? C.rose   : C.ink2,
      }}
      title={`Waiting ${label} since receipt`}
      data-testid={over48 ? 'intake-waiting-over48' : 'intake-waiting'}
    >
      <Clock size={11} /> {label}
    </span>
  );
}

// ─── Row ──────────────────────────────────────────────────────
function LogRow({ row, onPromote, onMarkNoise, showActions }) {
  const kind = row.classification || 'unknown';
  const api_base = process.env.REACT_APP_BACKEND_URL || '';
  return (
    <tr className="border-b" style={{ borderColor: C.border }} data-testid={`intake-row-${row.gmail_message_id}`}>
      <td className="py-2 pl-3 pr-2 text-[12px] whitespace-nowrap" style={{ color: C.ink2 }}>
        {row.received_at ? fmtDateTime(row.received_at) : '—'}
      </td>
      <td className="py-2 px-2"><ClassChip kind={kind} /></td>
      <td className="py-2 px-2 text-[13px]" style={{ color: C.ink }}>
        <div className="truncate max-w-[220px]" title={row.from}>{row.candidate_name || '—'}</div>
        <div className="text-[11px] truncate max-w-[220px]" style={{ color: C.soft }} title={row.from}>{row.from}</div>
      </td>
      <td className="py-2 px-2 text-[12px]" style={{ color: C.ink }}>
        <div className="truncate max-w-[300px]" title={row.subject}>{row.subject || '—'}</div>
        <div className="text-[11px] flex items-center gap-2" style={{ color: C.soft }}>
          {row.candidate_applied_role && (
            <span className="px-1 rounded" style={{ background: '#EEF0F8', color: C.plum, fontWeight: 500 }}
                  data-testid={`intake-role-${row.gmail_message_id}`}>
              {row.candidate_applied_role}
            </span>
          )}
          {row.candidate_has_resume && row.candidate_public_id && (
            <a href={`${api_base}/api/hr/candidates/${row.candidate_public_id}/resume`}
               target="_blank" rel="noreferrer"
               className="underline"
               style={{ color: C.plum }}
               data-testid={`intake-resume-${row.gmail_message_id}`}>
              📄 Resume
            </a>
          )}
        </div>
      </td>
      <td className="py-2 px-2"><WaitingBadge hours={row.waiting_hours} resolved={row.resolved} /></td>
      <td className="py-2 pr-3 pl-2 text-right">
        {showActions && !row.resolved && (kind === 'unknown' || kind === 'direct_application' || kind === 'indeed_application') && (
          <div className="inline-flex gap-1">
            <Button
              size="sm" variant="outline" className="h-7 px-2 text-[11px] gap-1"
              onClick={() => onPromote(row)}
              data-testid={`intake-promote-${row.gmail_message_id}`}
            >
              <ArrowRightCircle size={12} /> Acknowledge
            </Button>
            <Button
              size="sm" variant="outline" className="h-7 px-2 text-[11px] gap-1"
              onClick={() => onMarkNoise(row)}
              data-testid={`intake-noise-${row.gmail_message_id}`}
              style={{ color: C.rose }}
            >
              <Ban size={12} /> Noise
            </Button>
          </div>
        )}
      </td>
    </tr>
  );
}

// ─── Promote dialog ───────────────────────────────────────────
function PromoteDialog({ row, open, onOpenChange, onDone }) {
  const isDirect = row?.classification === 'direct_application';
  const [form, setForm] = useState({
    name: '', email: '', phone: '', applied_role: 'SLP', preferred_clinics: [], source: 'Direct',
  });
  useEffect(() => {
    if (row) {
      setForm({
        name: row.candidate_name || '',
        email: (row.from || '').match(/<([^>]+)>/)?.[1] || row.from || '',
        phone: '', applied_role: 'SLP', preferred_clinics: [], source: 'Direct',
      });
    }
  }, [row]);
  const submit = async () => {
    try {
      const payload = isDirect ? {} : form;
      const { data } = await api.post(
        `/hr/intake/${encodeURIComponent(row.gmail_message_id)}/promote`,
        payload
      );
      toast.success(isDirect ? 'Marked as acknowledged' : 'Candidate created');
      onDone?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(errMsg(e, 'Promote failed'));
    }
  };
  if (!row) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="intake-promote-dialog">
        <DialogHeader>
          <DialogTitle style={{ color: C.ink }}>
            {isDirect ? 'Acknowledge direct email' : 'Promote to candidate'}
          </DialogTitle>
          <DialogDescription className="text-[12px]" style={{ color: C.soft }}>
            {isDirect
              ? 'Clear this direct email from the review queue and stamp the linked candidate as reviewed.'
              : 'Create a new candidate row in the pipeline from this unclassified email.'}
          </DialogDescription>
        </DialogHeader>
        <div className="text-[12px] space-y-3">
          <div className="rounded p-2" style={{ background: C.paper, border: `1px solid ${C.border}` }}>
            <div><span style={{ color: C.soft }}>From:</span> <span style={{ color: C.ink }}>{row.from}</span></div>
            <div><span style={{ color: C.soft }}>Subject:</span> <span style={{ color: C.ink }}>{row.subject}</span></div>
          </div>
          {isDirect ? (
            <div style={{ color: C.ink2 }}>
              A candidate row was already created for this direct email. Clicking
              "Acknowledge" clears it from the review queue.
            </div>
          ) : (
            <>
              <div>
                <Label className="text-[11px]">Name</Label>
                <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                       data-testid="intake-promote-name" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[11px]">Email</Label>
                  <Input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                         data-testid="intake-promote-email" />
                </div>
                <div>
                  <Label className="text-[11px]">Phone</Label>
                  <Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                         data-testid="intake-promote-phone" />
                </div>
              </div>
              <div>
                <Label className="text-[11px]">Applied role</Label>
                <Select value={form.applied_role} onValueChange={v => setForm(f => ({ ...f, applied_role: v }))}>
                  <SelectTrigger data-testid="intake-promote-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="intake-promote-cancel">Cancel</Button>
          <Button onClick={submit} data-testid="intake-promote-submit"
                  style={{ background: C.plum, color: 'white' }}>
            {isDirect ? 'Acknowledge' : 'Create candidate'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Mark noise dialog ────────────────────────────────────────
function NoiseDialog({ row, open, onOpenChange, onDone }) {
  const [note, setNote] = useState('');
  useEffect(() => { if (row) setNote(''); }, [row]);
  const submit = async () => {
    try {
      await api.post(
        `/hr/intake/${encodeURIComponent(row.gmail_message_id)}/mark-noise`,
        { note }
      );
      toast.success('Marked as noise');
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(errMsg(e, 'Mark-noise failed'));
    }
  };
  if (!row) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="intake-noise-dialog">
        <DialogHeader>
          <DialogTitle style={{ color: C.ink }}>Mark as noise</DialogTitle>
          <DialogDescription className="text-[12px]" style={{ color: C.soft }}>
            Dismiss this email from the review queue. If a candidate was auto-created, it will be moved to "Not Moved Forward".
          </DialogDescription>
        </DialogHeader>
        <div className="text-[12px] space-y-3">
          <div className="rounded p-2" style={{ background: C.paper, border: `1px solid ${C.border}` }}>
            <div><span style={{ color: C.soft }}>Subject:</span> <span style={{ color: C.ink }}>{row.subject}</span></div>
          </div>
          <div>
            <Label className="text-[11px]">Reason (optional)</Label>
            <Textarea rows={2} value={note} onChange={e => setNote(e.target.value)}
                      placeholder="Why is this not a real application?"
                      data-testid="intake-noise-note" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} data-testid="intake-noise-submit"
                  style={{ background: C.rose, color: 'white' }}>
            Mark noise
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main ─────────────────────────────────────────────────────
export default function IntakeLog() {
  const [tab, setTab] = useState('review');   // review | log
  const [reviewRows, setReviewRows] = useState([]);
  const [logRows, setLogRows] = useState([]);
  const [logCounts, setLogCounts] = useState({});
  const [lastPollAt, setLastPollAt] = useState(null);
  const [reviewCount, setReviewCount] = useState(0);
  const [over48, setOver48] = useState(0);
  const [loading, setLoading] = useState(true);
  const [classFilter, setClassFilter] = useState('all');
  const [busy, setBusy] = useState(false);
  const [promoteRow, setPromoteRow] = useState(null);
  const [noiseRow, setNoiseRow] = useState(null);
  const [conn, setConn] = useState({ connected: false, sender_email: null, connected_at: null });
  const [connBusy, setConnBusy] = useState(false);

  const loadConn = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/gmail/status?target=hr_intake');
      setConn({
        connected: !!data.connected,
        sender_email: data.sender_email,
        connected_at: data.connected_at,
      });
    } catch (_) {
      setConn({ connected: false, sender_email: null, connected_at: null });
    }
  }, []);

  const connectMailbox = async () => {
    setConnBusy(true);
    try {
      const { data } = await api.get('/admin/gmail/connect?target=hr_intake');
      if (data?.authorization_url) {
        toast.info('Opening Google consent — sign in AS careers@thrivespeech.net');
        window.location.href = data.authorization_url;
      } else {
        toast.error('Could not start OAuth flow');
      }
    } catch (e) {
      toast.error(errMsg(e, 'Connect failed'));
    } finally {
      setConnBusy(false);
    }
  };

  const disconnectMailbox = async () => {
    if (!window.confirm('Disconnect the careers@ mailbox? The poller will stop until reconnected.')) return;
    try {
      await api.post('/admin/gmail/disconnect?target=hr_intake');
      toast.success('Mailbox disconnected');
      loadConn();
    } catch (e) {
      toast.error(errMsg(e, 'Disconnect failed'));
    }
  };

  const loadReview = useCallback(async () => {
    try {
      const { data } = await api.get('/hr/intake/review-queue');
      setReviewRows(data.rows || []);
      setReviewCount(data.count || 0);
      const over = (data.rows || []).filter(r => (r.waiting_hours || 0) > 48).length;
      setOver48(over);
    } catch (e) {
      toast.error(errMsg(e, 'Could not load review queue'));
    }
  }, []);

  const loadLog = useCallback(async () => {
    try {
      const params = {};
      if (classFilter !== 'all') params.classification = classFilter;
      const { data } = await api.get('/hr/intake/log', { params });
      setLogRows(data.rows || []);
      setLogCounts(data.counts || {});
      setLastPollAt(data.last_poll_at || null);
    } catch (e) {
      toast.error(errMsg(e, 'Could not load intake log'));
    }
  }, [classFilter]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([loadReview(), loadLog()]);
    setLoading(false);
    // Nudge sidebar to refresh its badge.
    window.dispatchEvent(new Event('hr-intake:changed'));
  }, [loadReview, loadLog]);

  useEffect(() => { loadAll(); loadConn(); }, [loadAll, loadConn]);

  // If the user just came back from an OAuth round-trip, show a toast
  // and refresh state. Query params: ?gmail_connected=1|error&target=hr_intake
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const status = p.get('gmail_connected');
    const target = p.get('target');
    if (!status) return;
    if (target && target !== 'hr_intake') return;
    if (status === '1') {
      toast.success(`Connected mailbox: ${p.get('sender') || 'careers@'}`);
    } else {
      toast.error(`Connect failed: ${p.get('reason') || 'unknown'}${p.get('detail') ? ' — ' + p.get('detail') : ''}`);
    }
    // Strip the params so a page refresh doesn't repeat the toast.
    const url = new URL(window.location.href);
    ['gmail_connected', 'target', 'sender', 'reason', 'detail'].forEach(k => url.searchParams.delete(k));
    window.history.replaceState({}, '', url.toString());
    loadConn();
  }, [loadConn]);

  const runPoll = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/hr/intake/run', {});
      const s = data.summary || {};
      toast.success(
        `Polled ${s.polled || 0}: +${s.candidates_new || 0} new, ` +
        `${s.candidates_linked || 0} linked, ${s.noise || 0} noise, ` +
        `${s.unknown || 0} unknown, ${s.duplicates || 0} dupes`
      );
      await loadAll();
    } catch (e) {
      toast.error(errMsg(e, 'Poll failed — check the mailbox connection'));
    } finally {
      setBusy(false);
    }
  };

  const handleActionDone = async () => {
    await loadAll();
  };

  const rowsForTab = tab === 'review' ? reviewRows : logRows;
  const showActions = tab === 'review';

  const summary = useMemo(() => ({
    review: reviewCount,
    over48,
    indeed_app: logCounts.indeed_application || 0,
    direct_app: logCounts.direct_application || 0,
    noise: logCounts.indeed_noise || 0,
    unknown: logCounts.unknown || 0,
  }), [reviewCount, over48, logCounts]);

  return (
    <div className="space-y-4" data-testid="hr-intake-log">
      {/* Header + poll button */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="text-[13px]" style={{ color: C.soft }}>
          Last poll:&nbsp;
          <span style={{ color: C.ink }} data-testid="intake-last-poll">
            {lastPollAt ? fmtDateTime(lastPollAt) : 'never — waiting for scheduler'}
          </span>
        </div>

        {/* Mailbox connection pill */}
        <div className="flex items-center gap-1 text-[11px] px-2 py-1 rounded"
             data-testid="intake-mailbox-status"
             style={{
               background: conn.connected ? '#E4F1EA' : '#FBF3E0',
               color: conn.connected ? C.teal : '#7A5E22',
               border: `1px solid ${conn.connected ? '#C7DCC9' : '#EBD9A6'}`,
             }}>
          {conn.connected
            ? (<><Link2 size={11} /> {conn.sender_email || 'mailbox connected'}</>)
            : (<><Link2Off size={11} /> Mailbox not connected</>)}
        </div>

        <div className="ml-auto flex items-center gap-2">
          {conn.connected ? (
            <Button variant="ghost" size="sm" onClick={disconnectMailbox}
                    data-testid="intake-disconnect"
                    className="gap-1 text-[11px]"
                    style={{ color: C.soft }}>
              Disconnect
            </Button>
          ) : (
            <Button variant="outline" size="sm" onClick={connectMailbox} disabled={connBusy}
                    data-testid="intake-connect-mailbox"
                    className="gap-1"
                    style={{ borderColor: C.gold, color: C.gold }}>
              <Link2 size={13} /> {connBusy ? 'Opening…' : 'Connect careers@'}
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={loadAll}
                  data-testid="intake-refresh" className="gap-1">
            <RefreshCw size={13} /> Refresh
          </Button>
          <Button size="sm" onClick={runPoll} disabled={busy || !conn.connected}
                  data-testid="intake-run-now"
                  title={conn.connected ? '' : 'Connect the mailbox first'}
                  style={{ background: C.plum, color: 'white', opacity: conn.connected ? 1 : 0.6 }}
                  className="gap-1">
            <Sparkles size={13} /> {busy ? 'Polling…' : 'Poll now'}
          </Button>
        </div>
      </div>

      {!conn.connected && (
        <Card className="p-3 border flex items-start gap-2"
              data-testid="intake-connect-banner"
              style={{ borderColor: '#EBD9A6', background: '#FBF3E0' }}>
          <AlertTriangle size={14} style={{ color: '#7A5E22', marginTop: 2 }} />
          <div className="text-[12px] flex-1" style={{ color: '#7A5E22' }}>
            <div className="font-semibold" style={{ color: C.ink }}>Connect the careers@ mailbox to start polling</div>
            The Gmail-API poller is idle until this is connected. Click "Connect careers@" above — an incognito window is recommended so you can sign in <b>as careers@thrivespeech.net</b> (not d.sekhon@) during the Google consent step.
          </div>
        </Card>
      )}

      {/* Tile row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card className="p-3 border" style={{ borderColor: C.border }} data-testid="tile-review">
          <div className="text-[10.5px] uppercase tracking-widest font-semibold" style={{ color: C.gold }}>Needs review</div>
          <div className="mt-1 flex items-baseline gap-2">
            <div className="font-serif text-3xl" style={{ color: C.ink }}>{summary.review}</div>
            {summary.over48 > 0 && (
              <span className="text-[11px] px-1.5 py-0.5 rounded font-semibold"
                    style={{ background: '#F6E4E4', color: C.rose }}
                    data-testid="tile-review-over48">
                {summary.over48} &gt;48h
              </span>
            )}
          </div>
          <div className="text-[11px] mt-1" style={{ color: C.soft }}>Unknown + parked direct emails</div>
        </Card>
        <Card className="p-3 border" style={{ borderColor: C.border }}>
          <div className="text-[10.5px] uppercase tracking-widest font-semibold" style={{ color: C.plum }}>Indeed apps</div>
          <div className="font-serif text-3xl" style={{ color: C.ink }}>{summary.indeed_app}</div>
          <div className="text-[11px] mt-1" style={{ color: C.soft }}>Last 14 days</div>
        </Card>
        <Card className="p-3 border" style={{ borderColor: C.border }}>
          <div className="text-[10.5px] uppercase tracking-widest font-semibold" style={{ color: C.plum }}>Direct emails</div>
          <div className="font-serif text-3xl" style={{ color: C.ink }}>{summary.direct_app}</div>
          <div className="text-[11px] mt-1" style={{ color: C.soft }}>Last 14 days</div>
        </Card>
        <Card className="p-3 border" style={{ borderColor: C.border }}>
          <div className="text-[10.5px] uppercase tracking-widest font-semibold" style={{ color: C.soft }}>Noise (skipped)</div>
          <div className="font-serif text-3xl" style={{ color: C.ink2 }}>{summary.noise}</div>
          <div className="text-[11px] mt-1" style={{ color: C.soft }}>Indeed billing / digests / chat</div>
        </Card>
        <Card className="p-3 border" style={{ borderColor: C.border }}>
          <div className="text-[10.5px] uppercase tracking-widest font-semibold" style={{ color: C.gold }}>Unknown</div>
          <div className="font-serif text-3xl" style={{ color: C.ink }}>{summary.unknown}</div>
          <div className="text-[11px] mt-1" style={{ color: C.soft }}>Parked in Review Queue</div>
        </Card>
      </div>

      {/* Sub-tabs */}
      <div className="border-b flex gap-1" style={{ borderColor: C.border }}>
        {[
          { key: 'review', label: `Review queue${summary.review ? ` (${summary.review})` : ''}`, icon: AlertTriangle },
          { key: 'log',    label: 'All intake log', icon: Inbox },
        ].map(t => {
          const Icon = t.icon;
          const active = t.key === tab;
          return (
            <button key={t.key} onClick={() => setTab(t.key)}
                    data-testid={`intake-subtab-${t.key}`}
                    className="px-4 py-2 flex items-center gap-2 text-sm border-b-2 transition-colors"
                    style={{
                      borderColor: active ? C.gold : 'transparent',
                      color: active ? C.ink : C.ink2,
                      fontWeight: active ? 600 : 400,
                    }}>
              <Icon size={13} strokeWidth={1.75} /> {t.label}
            </button>
          );
        })}
        {tab === 'log' && (
          <div className="ml-auto pb-1.5 flex items-center gap-2">
            <Filter size={12} style={{ color: C.soft }} />
            <Select value={classFilter} onValueChange={setClassFilter}>
              <SelectTrigger className="h-7 text-[12px] w-[180px]"
                             data-testid="intake-class-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All classifications</SelectItem>
                <SelectItem value="indeed_application">Indeed application</SelectItem>
                <SelectItem value="direct_application">Direct email</SelectItem>
                <SelectItem value="indeed_noise">Indeed noise</SelectItem>
                <SelectItem value="unknown">Unknown</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Table */}
      <Card className="border overflow-hidden" style={{ borderColor: C.border }}>
        {loading ? (
          <div className="p-6 text-sm" style={{ color: C.soft }}>Loading…</div>
        ) : rowsForTab.length === 0 ? (
          <div className="p-8 text-center text-sm space-y-1">
            <Inbox size={24} className="mx-auto" style={{ color: C.soft }} />
            <div style={{ color: C.ink2 }}>
              {tab === 'review'
                ? 'Nothing waiting — Indeed & careers@ are quiet.'
                : 'No polled messages in the last 14 days yet.'}
            </div>
            {tab === 'log' && (
              <div className="text-[11px]" style={{ color: C.soft }}>
                If Indeed is set up and applications aren't showing, click "Poll now" to force a cycle.
              </div>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]" data-testid="intake-table">
              <thead>
                <tr className="border-b" style={{ borderColor: C.border, background: C.paper }}>
                  <th className="py-2 pl-3 pr-2 text-left font-semibold" style={{ color: C.ink2 }}>Received</th>
                  <th className="py-2 px-2 text-left font-semibold" style={{ color: C.ink2 }}>Class</th>
                  <th className="py-2 px-2 text-left font-semibold" style={{ color: C.ink2 }}>Sender</th>
                  <th className="py-2 px-2 text-left font-semibold" style={{ color: C.ink2 }}>Subject / Reason</th>
                  <th className="py-2 px-2 text-left font-semibold" style={{ color: C.ink2 }}>Waiting</th>
                  <th className="py-2 pr-3 pl-2 text-right font-semibold" style={{ color: C.ink2 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rowsForTab.map(row => (
                  <LogRow key={row.gmail_message_id}
                          row={row}
                          onPromote={setPromoteRow}
                          onMarkNoise={setNoiseRow}
                          showActions={showActions} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <PromoteDialog row={promoteRow}
                     open={!!promoteRow}
                     onOpenChange={o => !o && setPromoteRow(null)}
                     onDone={handleActionDone} />
      <NoiseDialog row={noiseRow}
                   open={!!noiseRow}
                   onOpenChange={o => !o && setNoiseRow(null)}
                   onDone={handleActionDone} />
    </div>
  );
}
